import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components
import { ChartGrid } from '@mobilitystore/charts-interface';
import CustomCell from '../../../../msreact-kit/html-components/custom-cell';

//Utils
import { getCountryOptions } from '../../../../components/dashboard/UnifiedSellerDashboard/utils';

// Assets
import { CDN_LINK } from '../../../../assets/img';

// Constants
import idConstants from '../../../../data/idConstants';

// Actions
import {
  getAllSupplierDetailsWithPagination,
  deleteSupplierUser
} from '../../../../actions/backoffice-service-action';
import { setSnackbar } from '../../../../actions/generic-action';
import { updateSupplierAttributes } from '../../../../actions/user-service-action';

//CSS
import './index.scss';

const CustomButtonComponent = (props) => {
  const dispatch = useDispatch();

  const { content, supplierAttributes, countryList } = useSelector((state) => {
    return {
      content: state.ContentReducer.content,
      supplierAttributes: state.UserServiceReducer.supplierAttributes,
      countryList: state.CountryReducer.countryList
    };
  });

  const totalUsers = props.api.getDisplayedRowCount();

  const allCountryOptions = getCountryOptions(countryList, 'isdCode');

  const supplierContent =
    content?.components?.user_management?.supplier_management?.supplier_details;

  const handleDeleteSupplierUserId = (supplierUserMappingId) => {
    dispatch(deleteSupplierUser(supplierUserMappingId))
      .then(() => {
        dispatch(getAllSupplierDetailsWithPagination(props.data.supplierName));
        dispatch(
          setSnackbar({
            showSnackbar: true,
            message: supplierContent?.delete?.message,
            severity: 'success'
          })
        );
      })
      .catch((error) => {
        dispatch(
          setSnackbar({
            showSnackbar: true,
            message: error?.response?.data?.message,
            severity: 'error'
          })
        );
      });
  };

  const modifyAttribute = (attributes, name, key, value) => {
    attributes?.forEach((attr) => {
      if (attr.attributeType === 'COMBINE')
        modifyAttribute(attr.attributes, name, key, value);

      if (attr.name === name) {
        attr[key] = value;
      }
    });
  };

  const handleEditSupplierUser = (
    supplierAttributes,
    data,
    content,
    allCountryOptions
  ) => {
    Object.entries(data).forEach((item) => {
      const name = item[0];
      const value = item[1];
      if (name === 'contactNo') {
        const isdCode = value?.split(' ')[0];
        const contactNo = value?.split(' ')[1];

        const isdCodeOption = allCountryOptions?.find(
          (item) => item.label === isdCode
        );
        modifyAttribute(supplierAttributes, 'isdCode', 'value', isdCodeOption);
        modifyAttribute(supplierAttributes, 'contactNo', 'value', contactNo);
      } else {
        modifyAttribute(supplierAttributes, name, 'value', value);
      }
      modifyAttribute(
        supplierAttributes,
        'addSupplier',
        'displayName',
        content?.update?.title
      );
      if (data?.emailVerified === 'true')
        modifyAttribute(supplierAttributes, 'emailId', 'disabled', true);
      else modifyAttribute(supplierAttributes, 'emailId', 'disabled', false);
    });
    dispatch(updateSupplierAttributes(supplierAttributes));
  };

  return (
    <div>
      <div className='supplier-grid__action'>
        <img
          src={`${CDN_LINK}/${supplierContent?.edit?.icon}`}
          alt={supplierContent?.edit?.alt}
          onClick={() => {
            handleEditSupplierUser(
              supplierAttributes,
              props.data,
              supplierContent,
              allCountryOptions
            );
          }}
          tabIndex={0}
          role='button'
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ')
              handleEditSupplierUser(
                supplierAttributes,
                props.data,
                supplierContent,
                allCountryOptions
              );
          }}
        />
        <img
          src={`${CDN_LINK}/${supplierContent?.delete?.icon}`}
          className={totalUsers === 1 ? 'supplier-grid__action__hidden' : ''}
          alt={supplierContent?.delete?.alt}
          onClick={() => {
            totalUsers > 1 &&
              handleDeleteSupplierUserId(props.data.supplierUserMappingId);
          }}
          tabIndex={0}
          role='button'
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ')
              totalUsers > 1 &&
                handleDeleteSupplierUserId(props.data.supplierUserMappingId);
          }}
        />
      </div>
    </div>
  );
};

const getStatusIcon = (value, content) => {
  if (value === 'true') {
    return {
      className: content?.success?.icon?.class,
      title: content?.success?.icon?.alt,
      alt: content?.success?.icon?.alt
    };
  }
  return {
    className: content?.pending?.icon?.class,
    title: content?.pending?.icon?.alt,
    alt: content?.pending?.icon?.alt
  };
};

const CustomIcon = (cellProps) => {
  const { content } = useSelector((state) => {
    return {
      content: state.ContentReducer.content
    };
  });

  const supplierContent =
    content?.components?.user_management?.supplier_management?.supplier_details;
  const { className, title, alt } = getStatusIcon(cellProps.value, supplierContent);
  return (
    <div className='supplier-grid__status-cell'>
      <i
        className={`supplier-grid__status-cell__icon ${className}`}
        alt={alt}
        title={title}
      />
    </div>
  );
};

export const SupplierUserDetailsGrid = ({ data, mode }) => {
  const colDefs = [
    {
      field: 'firstName',
      headerName: 'Supplier First Name',
      cellRenderer: CustomCell
    },
    {
      field: 'lastName',
      headerName: 'Supplier Last Name',
      flex: 1,
      cellRenderer: CustomCell
    },
    {
      field: 'emailId',
      headerName: 'Email',
      flex: 1,
      cellRenderer: CustomCell
    },
    {
      field: 'contactNo',
      headerName: 'Phone',
      flex: 1,
      cellRenderer: CustomCell
    }
  ];

  if (mode !== idConstants.supplierDetails.mode.view) {
    const actionButtons = {
      field: 'button',
      headerName: 'Actions',
      cellRenderer: CustomButtonComponent,
      maxWidth: 120
    };

    colDefs.push(actionButtons);
  }

  if (mode !== idConstants.supplierDetails.mode.add) {
    colDefs.splice(3, 0, {
      field: 'emailVerified',
      headerName: 'Status',
      flex: 1,
      cellRenderer: CustomIcon
    });
  }

  const gridProps = {
    rowData: data,
    columnDefs: colDefs,
    suppressDragLeaveHidesColumns: true,
    suppressMovableColumns: true,
    filter: 'agTextColumnFilter'
  };

  return (
    <div className='supplier-grid__container'>
      <div className='supplier-grid__table-wrapper'>
        <ChartGrid className='supplier-grid__ag-table' gridProps={gridProps} />
      </div>
    </div>
  );
};
